import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import axios from "axios";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  FiUsers,
  FiChevronsLeft,
  FiChevronsRight,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiX,
  FiEye,
  FiDownload,
  FiPhone,
  FiSearch,
} from "react-icons/fi";
import {
  fetchBeneficiarySurveys,
  fetchAllBeneficiarySurveys,
} from "../../../redux/actions/survey-management/beneficiarySurveyActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  fetchWards,
  clearMunicipalities,
  clearWards,
} from "../../../redux/actions/internal/internalActions";
import { Divide } from "react-feather";

const BeneficiarySurvey = () => {
  const dispatch = useDispatch();
  const { loading, surveys, pagination, surveyStats } = useSelector(
    (state) =>
      state.beneficiarySurvey || {
        loading: false,
        surveys: [],
        pagination: { page: 1, limit: 10, totalRecords: 0, totalPages: 0 },
        surveyStats: { totalSurvey: 0, surveyedCount: 0, pendingCount: 0 },
      },
  );

  const { districts, municipalities, wards } = useSelector(
    (state) =>
      state.internal || { districts: [], municipalities: [], wards: [] },
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isBulkExporting, setIsBulkExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const limitOptions = [
    10, 20, 50, 100, 200, 500, 1000, 1500, 2000, 3000, 4000,
  ];
  const [filters, setFilters] = useState({
    district_id: "",
    municipality_id: "",
    ward_id: "",
    ward_name: "",
    search: "",
    eligibility_status: "",
  });
  const [imageModal, setImageModal] = useState({
    open: false,
    url: "",
    title: "",
  });

  useEffect(() => {
    dispatch(fetchBeneficiarySurveys(currentPage, limit, filters));
    dispatch(fetchDistricts());
  }, [
    dispatch,
    currentPage,
    limit,
    filters.district_id,
    filters.municipality_id,
    filters.ward_name,
    filters.search,
    filters.eligibility_status,
  ]);

  useEffect(() => {
    if (filters.district_id) {
      dispatch(fetchMunicipalities(filters.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [filters.district_id, dispatch]);

  useEffect(() => {
    if (filters.municipality_id) {
      dispatch(fetchWards(filters.municipality_id));
    } else {
      dispatch(clearWards());
    }
  }, [filters.municipality_id, dispatch]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      dispatch(fetchBeneficiarySurveys(page, limit, filters));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchBeneficiarySurveys(currentPage, limit, filters));
    toast.success("Data refreshed!");
  };

  const openFile = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleExportToExcel = async () => {
    if (surveys.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = [
      "ID",
      "Applicant Name",
      "Address",
      "Pincode",
      "Scheme",
      "Age",
      "Gender",
      "Caste",
      "Father's Name",
      "Mobile No.",
      "District",
      "Municipality",
      "Ward",
      "Resident of ULB",
      "Constituency",
      "Survey Officer Name",
      "Survey Officer ID",
      "Survey Officer Phone",
      "DLS Status",
      "Part No.",
      "Serial No.",
      "BPL ID",
      "BPL Member ID",
      "Sanction Order No.",
      "Ration Card No.",
      "EPIC No.",
      "Beneficiary ID.",
      "Beneficiary No.",
      "Latitude",
      "Longitude",
      "Eligibility Status",
      "Ineligible Reason",
      "Voter Card Image",
      "Valid Voter Card",
      "Reason - Voter Card Not Collected",
      "Aadhar Card Image",
      "Valid Aadhar Card",
      "Reason - Aadhaar Card Not Collected",
      "Receives other Govt. Pension",
      "Other Govt. Pension Name",
      // "Other Govt. Pension Image",
      // "Other Govt. Pension Remarks",
      "Geo Tagged Photo Image",
      "Comments",
      "DLC expiry date",
    ];

    // Map data to rows with change tracking
    const rows = surveys.map((item) => {
      const getFieldValue = (fieldName) => {
        const changedField = item.changed_fields?.find(
          (f) => f.field === fieldName,
        );
        if (changedField) {
          return {
            value: changedField.new_value || changedField.old_value,
            isChanged: true,
            oldValue: changedField.old_value,
          };
        }
        return {
          value: item[fieldName],
          isChanged: false,
          oldValue: null,
        };
      };

      const applicantName = getFieldValue("applicant_name");
      const address = getFieldValue("address");
      const pincode = getFieldValue("pincode");
      const scheme = getFieldValue("scheme");
      const age = getFieldValue("age");
      const gender = getFieldValue("gender");
      const caste = getFieldValue("caste");
      const fhName = getFieldValue("fh_name");
      const mobile = getFieldValue("mobile_no");
      const district = getFieldValue("district_name");
      const municipality = getFieldValue("municipality_name");
      const ward = getFieldValue("ward");
      const lat = getFieldValue("latitude");
      const long = getFieldValue("longitude");
      const surOfficerName = getFieldValue("survey_officer_name");
      const surOfficerMobile = getFieldValue("survey_officer_phone");
      const surOfficerID = getFieldValue("survey_officer_name");

      const residentOfUlb = getFieldValue("resident_of_ulb");
      const constituency = getFieldValue("constituency");
      const dlsStatus = getFieldValue("dls_status");
      const partNo = getFieldValue("part_no");
      const srlNo = getFieldValue("srl_no");
      const bplID = getFieldValue("bpl_id");
      const bplMember = getFieldValue("bpl_member_id");

      const sanctionOrder = getFieldValue("sanction_order_no");
      const rationCard = getFieldValue("ration_card_no");
      const epicNo = getFieldValue("epic_no");
      const beneficiaryId = getFieldValue("beneficiary_id");
      const beneficiaryNo = getFieldValue("beneficiary_no");
      const eligibilityStatus = getFieldValue("eligibility_status");
      const ineligibleReason = getFieldValue("ineligible_reason");
      const validVoterCard = getFieldValue("valid_voter_card");
      const govtPension = getFieldValue("other_govt_pension");
      const govtPensionName = getFieldValue("other_govt_pension_name");
      const govtPensionRemarks = getFieldValue("other_govt_pension_remarks");

      const noVoterReason = getFieldValue("no_voter_card_collected_reason");
      const validAadharCard = getFieldValue("valid_aadhar_card");
      const noAadharReason = getFieldValue("no_aadhar_card_collected_reason");
      const comments = getFieldValue("specific_comments");
      const dlcExpiry = getFieldValue("dlc_expired_date");

      // Return array of objects with value and changed flag
      return [
        { value: item.id || "N/A", changed: false },
        {
          value: applicantName?.value || "N/A",
          changed: applicantName?.isChanged || false,
        },
        {
          value: address?.value || "N/A",
          changed: address?.isChanged || false,
        },
        {
          value: pincode?.value || "N/A",
          changed: pincode?.isChanged || false,
        },
        { value: scheme?.value || "N/A", changed: scheme?.isChanged || false },
        { value: age?.value || "N/A", changed: age?.isChanged || false },
        { value: gender?.value || "N/A", changed: gender?.isChanged || false },
        { value: caste?.value || "N/A", changed: caste?.isChanged || false },
        { value: fhName?.value || "N/A", changed: fhName?.isChanged || false },
        { value: mobile?.value || "N/A", changed: mobile?.isChanged || false },
        {
          value: district?.value || item.district_name || "N/A",
          changed: district?.isChanged || false,
        },
        {
          value: municipality?.value || item.municipality_name || "N/A",
          changed: municipality?.isChanged || false,
        },
        { value: ward?.value || "N/A", changed: ward?.isChanged || false },
        {
          value: residentOfUlb?.value || "N/A",
          changed: residentOfUlb?.isChanged || false,
        },
        {
          value: constituency?.value || "N/A",
          changed: constituency?.isChanged || false,
        },
        { value: item.survey_officer_name || "N/A", changed: false },
        { value: item.survey_officer_id || "N/A", changed: false },
        { value: item.survey_officer_phone || "N/A", changed: false },
        {
          value: dlsStatus?.value || "N/A",
          changed: dlsStatus?.isChanged || false,
        },
        { value: partNo?.value || "N/A", changed: partNo?.isChanged || false },
        { value: srlNo?.value || "N/A", changed: srlNo?.isChanged || false },
        { value: item.bpl_id || "N/A", changed: false },
        { value: item.bpl_member_id || "N/A", changed: false },
        {
          value: sanctionOrder?.value || "N/A",
          changed: sanctionOrder?.isChanged || false,
        },
        {
          value: rationCard?.value || "N/A",
          changed: rationCard?.isChanged || false,
        },
        { value: epicNo?.value || "N/A", changed: epicNo?.isChanged || false },
        {
          value: beneficiaryId?.value || "N/A",
          changed: beneficiaryId?.isChanged || false,
        },
        {
          value: beneficiaryNo?.value || "N/A",
          changed: beneficiaryNo?.isChanged || false,
        },
        {
          value: lat?.value || item.latitude || "N/A",
          changed: lat?.isChanged || false,
        },
        {
          value: long?.value || item.longitude || "N/A",
          changed: long?.isChanged || false,
        },
        {
          value: eligibilityStatus?.value || "N/A",
          changed: eligibilityStatus?.isChanged || false,
        },
        {
          value: ineligibleReason?.value || "N/A",
          changed: ineligibleReason?.isChanged || false,
        },
        { value: item.voter_card_collected || "N/A", changed: false },
        {
          value: validVoterCard?.value || "N/A",
          changed: validVoterCard?.isChanged || false,
        },
        { value: item.no_voter_card_collected_reason || "N/A", changed: false },
        { value: item.aadhar_card_collected || "N/A", changed: false },
        {
          value: validAadharCard?.value || "N/A",
          changed: validAadharCard?.isChanged || false,
        },
        {
          value:
            noAadharReason?.value ||
            item.no_aadhar_card_collected_reason ||
            "N/A",
          changed: noAadharReason?.isChanged || false,
        },
        {
          value: govtPension?.value || "N/A",
          changed: govtPension?.isChanged || false,
        },
        {
          value: govtPensionName?.value || "N/A",
          changed: govtPensionName?.isChanged || false,
        },
        // { value: item.other_govt_pension_uploaded || "N/A", changed: false },
        // { value: item.other_govt_pension_remarks || "N/A", changed: false },
        { value: item.geo_tagged_photo_captured || "N/A", changed: false },
        {
          value: comments?.value || "N/A",
          changed: comments?.isChanged || false,
        },
        {
          value: dlcExpiry?.value
            ? new Date(dlcExpiry.value).toLocaleDateString("en-IN")
            : "N/A",
          changed: dlcExpiry?.isChanged || false,
        },
      ];
    });

    // Create workbook using ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("BeneficiarySurvey");

    // Add header row with styling
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFB923C" },
    };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };

    // Add data rows with cell-by-cell coloring
    rows.forEach((rowData) => {
      const row = worksheet.addRow(rowData.map((cell) => cell.value));

      // Apply styling to each cell in the row
      row.eachCell((cell, colNumber) => {
        const cellIndex = colNumber - 1; // 0-based index
        const cellData = rowData[cellIndex];

        // Apply red background if cell is changed
        if (cellData && cellData.changed) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFCCCC" }, // Red background
          };
          cell.font = {
            color: { argb: "FF000000" }, // Black text for better contrast
            bold: true,
          };
        }

        // Apply border to all cells
        cell.border = {
          top: { style: "thin", color: { argb: "FFD3D3D3" } },
          left: { style: "thin", color: { argb: "FFD3D3D3" } },
          bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
          right: { style: "thin", color: { argb: "FFD3D3D3" } },
        };

        // Center alignment for all cells
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      });
    });

    // Set column widths
    const colWidths = [
      8, 25, 30, 12, 15, 8, 10, 12, 20, 15, 20, 25, 10, 18, 20, 25, 15, 18, 15,
      15, 15, 15, 18, 22, 18, 20, 18, 18, 15, 15, 18, 20, 25, 18, 25, 25, 35,
      35, 15, 20, 30, 20,
    ];

    worksheet.columns = colWidths.map((width) => ({
      width: width,
    }));

    // Freeze header row
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const date = new Date();
    const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}-${date.getFullYear()}`;

    saveAs(new Blob([buffer]), `Beneficiary_Survey_${dateStr}.xlsx`);
    toast.success("Export successful!");
  };

  const handleBulkExportToExcel = async () => {
    setIsBulkExporting(true);
    setExportProgress(0);

    try {
      // Simulate progress updates - SLOW INCREASE
      const progressInterval = setInterval(() => {
        setExportProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          const increment = Math.random() < 0.3 ? 1 : 0; // Only increase 30% of the time by 1
          return Math.min(prev + increment, 95);
        });
      }, 500); // 500ms interval

      toast.loading("Fetching all records...", {
        id: "bulk-export",
      });

      const allSurveys = await fetchAllBeneficiarySurveys(filters, 2000000);

      clearInterval(progressInterval);
      setExportProgress(100);

      if (allSurveys.length === 0) {
        toast.dismiss("bulk-export");
        toast.error("No data to export");
        setIsBulkExporting(false);
        setExportProgress(0);
        return;
      }

      const headers = [
        "Sl. no.",
        "ID",
        "Applicant Name",
        "Address",
        "Pincode",
        "Scheme",
        "Age",
        "Gender",
        "Caste",
        "Father's Name",
        "Mobile No.",
        "District",
        "Municipality",
        "Ward",
        "Resident of ULB",
        "Constituency",
        "Survey Officer Name",
        "Survey Officer ID",
        "Survey Officer Phone",
        "DLS Status",
        "Part No.",
        "Serial No.",
        "BPL ID",
        "BPL Member ID",
        "Sanction Order No.",
        "Ration Card No.",
        "EPIC No.",
        "Beneficiary ID.",
        "Beneficiary No.",
        "Latitude",
        "Longitude",
        "Eligibility Status",
        "Ineligible Reason",
        "Voter Card Image",
        "Valid Voter Card",
        "Reason - Voter Card Not Collected",
        "Aadhar Card Image",
        "Valid Aadhar Card",
        "Reason - Aadhaar Card Not Collected",
        "Receives other Govt. Pension",
        "Other Govt. Pension Name",
        "Geo Tagged Photo Image",
        "Comments",
        "DLC expiry date",
        "Updated Date",
      ];

      // Map data to rows with change tracking (array format for coloring)
      const rows = allSurveys.map((item, index) => {
        const getFieldValue = (fieldName) => {
          const changedField = item.changed_fields?.find(
            (f) => f.field === fieldName,
          );
          if (changedField) {
            return {
              value: changedField.new_value || changedField.old_value,
              isChanged: true,
              oldValue: changedField.old_value,
            };
          }
          return {
            value: item[fieldName],
            isChanged: false,
            oldValue: null,
          };
        };

        const applicantName = getFieldValue("applicant_name");
        const address = getFieldValue("address");
        const pincode = getFieldValue("pincode");
        const scheme = getFieldValue("scheme");
        const age = getFieldValue("age");
        const gender = getFieldValue("gender");
        const caste = getFieldValue("caste");
        const fhName = getFieldValue("fh_name");
        const mobile = getFieldValue("mobile_no");
        const district = getFieldValue("district_name");
        const municipality = getFieldValue("municipality_name");
        const ward = getFieldValue("ward");
        const lat = getFieldValue("latitude");
        const long = getFieldValue("longitude");
        const residentOfUlb = getFieldValue("resident_of_ulb");
        const constituency = getFieldValue("constituency");
        const dlsStatus = getFieldValue("dls_status");
        const partNo = getFieldValue("part_no");
        const srlNo = getFieldValue("srl_no");
        const bplMember = getFieldValue("bpl_member_id");
        const sanctionOrder = getFieldValue("sanction_order_no");
        const rationCard = getFieldValue("ration_card_no");
        const epicNo = getFieldValue("epic_no");
        const beneficiaryId = getFieldValue("beneficiary_id");
        const beneficiaryNo = getFieldValue("beneficiary_no");
        const eligibilityStatus = getFieldValue("eligibility_status");
        const ineligibleReason = getFieldValue("ineligible_reason");
        const validVoterCard = getFieldValue("valid_voter_card");
        const govtPension = getFieldValue("other_govt_pension");
        const govtPensionName = getFieldValue("other_govt_pension_name");
        const noVoterReason = getFieldValue("no_voter_card_collected_reason");
        const validAadharCard = getFieldValue("valid_aadhar_card");
        const noAadharReason = getFieldValue("no_aadhar_card_collected_reason");
        const comments = getFieldValue("specific_comments");
        const dlcExpiry = getFieldValue("dlc_expired_date");
        const voterCard = getFieldValue("voter_card_collected");
        const aadharCard = getFieldValue("aadhar_card_collected");
        const geoTagged = getFieldValue("geo_tagged_photo_captured");
        const updatedAt = getFieldValue("updated_at");

        // Return array of objects with value and changed flag
        return [
          { value: index + 1, changed: false }, // Sl. No.
          { value: item.id || "N/A", changed: false }, // ID
          {
            value: applicantName?.value || "N/A",
            changed: applicantName?.isChanged || false,
          },
          {
            value: address?.value || "N/A",
            changed: address?.isChanged || false,
          },
          {
            value: pincode?.value || "N/A",
            changed: pincode?.isChanged || false,
          },
          {
            value: scheme?.value || "N/A",
            changed: scheme?.isChanged || false,
          },
          { value: age?.value || "N/A", changed: age?.isChanged || false },
          {
            value: gender?.value || "N/A",
            changed: gender?.isChanged || false,
          },
          { value: caste?.value || "N/A", changed: caste?.isChanged || false },
          {
            value: fhName?.value || "N/A",
            changed: fhName?.isChanged || false,
          },
          {
            value: mobile?.value || "N/A",
            changed: mobile?.isChanged || false,
          },
          {
            value: district?.value || item.district_name || "N/A",
            changed: district?.isChanged || false,
          },
          {
            value: municipality?.value || item.municipality_name || "N/A",
            changed: municipality?.isChanged || false,
          },
          { value: ward?.value || "N/A", changed: ward?.isChanged || false },
          {
            value: residentOfUlb?.value || "N/A",
            changed: residentOfUlb?.isChanged || false,
          },
          {
            value: constituency?.value || "N/A",
            changed: constituency?.isChanged || false,
          },
          { value: item.survey_officer_name || "N/A", changed: false },
          { value: item.survey_officer_id || "N/A", changed: false },
          { value: item.survey_officer_phone || "N/A", changed: false },
          {
            value: dlsStatus?.value || "N/A",
            changed: dlsStatus?.isChanged || false,
          },
          {
            value: partNo?.value || "N/A",
            changed: partNo?.isChanged || false,
          },
          { value: srlNo?.value || "N/A", changed: srlNo?.isChanged || false },
          { value: item.bpl_id || "N/A", changed: false },
          {
            value: bplMember?.value || item.bpl_member_id || "N/A",
            changed: bplMember?.isChanged || false,
          },
          {
            value: sanctionOrder?.value || "N/A",
            changed: sanctionOrder?.isChanged || false,
          },
          {
            value: rationCard?.value || "N/A",
            changed: rationCard?.isChanged || false,
          },
          {
            value: epicNo?.value || "N/A",
            changed: epicNo?.isChanged || false,
          },
          {
            value: beneficiaryId?.value || "N/A",
            changed: beneficiaryId?.isChanged || false,
          },
          {
            value: beneficiaryNo?.value || "N/A",
            changed: beneficiaryNo?.isChanged || false,
          },
          {
            value: lat?.value || item.latitude || "N/A",
            changed: lat?.isChanged || false,
          },
          {
            value: long?.value || item.longitude || "N/A",
            changed: long?.isChanged || false,
          },
          {
            value: eligibilityStatus?.value || "N/A",
            changed: eligibilityStatus?.isChanged || false,
          },
          {
            value: ineligibleReason?.value || "N/A",
            changed: ineligibleReason?.isChanged || false,
          },
          {
            value: voterCard?.value || "N/A",
            changed: voterCard?.isChanged || false,
          },
          {
            value: validVoterCard?.value || "N/A",
            changed: validVoterCard?.isChanged || false,
          },
          {
            value:
              noVoterReason?.value ||
              item.no_voter_card_collected_reason ||
              "N/A",
            changed: noVoterReason?.isChanged || false,
          },
          {
            value: aadharCard?.value || "N/A",
            changed: aadharCard?.isChanged || false,
          },
          {
            value: validAadharCard?.value || "N/A",
            changed: validAadharCard?.isChanged || false,
          },
          {
            value:
              noAadharReason?.value ||
              item.no_aadhar_card_collected_reason ||
              "N/A",
            changed: noAadharReason?.isChanged || false,
          },
          {
            value: govtPension?.value || "N/A",
            changed: govtPension?.isChanged || false,
          },
          {
            value: govtPensionName?.value || "N/A",
            changed: govtPensionName?.isChanged || false,
          },
          {
            value: geoTagged?.value || "N/A",
            changed: geoTagged?.isChanged || false,
          },
          {
            value: comments?.value || "N/A",
            changed: comments?.isChanged || false,
          },
          {
            value: dlcExpiry?.value
              ? new Date(dlcExpiry.value).toLocaleDateString("en-IN")
              : "N/A",
            changed: dlcExpiry?.isChanged || false,
          },
          {
            value: updatedAt?.value
              ? new Date(updatedAt.value).toLocaleDateString("en-IN")
              : "N/A",
            changed: updatedAt?.isChanged || false,
          },
        ];
      });

      // Create workbook using ExcelJS for styling
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("BeneficiarySurvey");

      // Add header row with styling
      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFB923C" }, // Orange background
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };

      // Add data rows with cell-by-cell coloring
      rows.forEach((rowData) => {
        const row = worksheet.addRow(rowData.map((cell) => cell.value));

        // Apply styling to each cell in the row
        row.eachCell((cell, colNumber) => {
          const cellIndex = colNumber - 1;
          const cellData = rowData[cellIndex];

          // Apply light red background if cell is changed
          if (cellData && cellData.changed) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFCCCC" }, // Light cream red
            };
            cell.font = {
              color: { argb: "FF000000" }, // Black text for better readability
              bold: true,
            };
          }

          // Apply border to all cells
          cell.border = {
            top: { style: "thin", color: { argb: "FFD3D3D3" } },
            left: { style: "thin", color: { argb: "FFD3D3D3" } },
            bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
            right: { style: "thin", color: { argb: "FFD3D3D3" } },
          };

          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
        });
      });

      // Set column widths
      const colWidths = [
        8, 8, 25, 30, 12, 15, 8, 10, 12, 20, 15, 20, 25, 10, 18, 20, 25, 15, 18,
        15, 15, 15, 15, 18, 22, 18, 20, 18, 18, 15, 15, 18, 20, 25, 18, 25, 25,
        35, 35, 15, 20, 30, 20,
      ];

      worksheet.columns = colWidths.map((width) => ({
        width: width,
      }));

      // Freeze header row
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      // Generate and download file
      const buffer = await workbook.xlsx.writeBuffer();
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${date.getFullYear()}`;

      saveAs(new Blob([buffer]), `Beneficiary_Survey_${dateStr}.xlsx`);
      toast.success("Export successful!", {
        id: "bulk-export",
      });
    } catch (error) {
      toast.error("Failed to export", {
        id: "bulk-export",
      });
      console.error("Bulk export error:", error);
    } finally {
      setTimeout(() => {
        setIsBulkExporting(false);
        setExportProgress(0);
      }, 1000);
    }
  };
  const getFieldValue = (item, fieldName) => {
    const changedField = item.changed_fields?.find(
      (f) => f.field === fieldName,
    );
    if (changedField) {
      return {
        value: changedField.new_value || changedField.old_value,
        isChanged: true,
        oldValue: changedField.old_value,
      };
    }
    return {
      value: item[fieldName],
      isChanged: false,
      oldValue: null,
    };
  };
  const getChangedCellClass = (isChanged) =>
    `py-1.5 px-2 border border-gray-300 dark:border-gray-600 ${
      isChanged ? "bg-red-100 dark:bg-red-900/20" : ""
    }`;
  return (
    <div className="p-2 md:p-4 max-w-full mx-auto">
      {" "}
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white">
            Beneficiary Survey
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage beneficiary survey data
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleBulkExportToExcel}
            disabled={isBulkExporting}
            className="relative flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden min-w-[180px]"
          >
            {isBulkExporting && (
              <div
                className="absolute left-0 top-0 h-full bg-red-400/30 transition-all duration-300"
                style={{ width: `${exportProgress}%` }}
              />
            )}
            {isBulkExporting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white relative z-10"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="relative z-10">
                  Exporting {exportProgress}%
                </span>
              </>
            ) : (
              <>
                <FiDownload className="w-4 h-4 relative z-10" />
                <span className="relative z-10">Export All Records</span>
              </>
            )}
          </button>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleExportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            <FiDownload className="w-4 h-4" />
            Export to Excel
          </button>
        </div>
      </div>
      {/* Table Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <FiUsers className="w-5 h-5 text-gray-800 dark:text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Beneficiary Surveys
            </h2>
            <span className="text-sm font-medium text-orange-800 dark:text-orange-200 border border-orange-200 dark:border-orange-700 px-3 py-1 rounded-md">
              TOTAL: {pagination.totalRecords || 0}
            </span>
            <div className="flex items-center gap-2 ml-4">
              <label className="text-sm text-gray-800 dark:text-gray-600">
                Show:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border-2 border-orange-200 dark:border-orange-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium min-w-[80px]"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* First Row - District and Municipality */}
          <div className="flex items-center gap-3 mb-3">
            <select
              value={filters.district_id}
              onChange={(e) => {
                const value = e.target.value;
                setFilters({
                  district_id: value,
                  municipality_id: "",
                  ward_id: "",
                  ward_name: "",
                  search: filters.search,
                  eligibility_status: filters.eligibility_status,
                });
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[120px]"
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>

            <select
              value={filters.municipality_id}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  municipality_id: e.target.value,
                });
                setCurrentPage(1);
              }}
              disabled={!filters.district_id}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[180px] disabled:opacity-50"
            >
              <option value="">All Municipalities</option>
              {municipalities.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>

            <select
              value={filters.ward_id}
              onChange={(e) => {
                const wardId = e.target.value;
                const selectedWard = wards.find(
                  (w) => w.id === parseInt(wardId),
                );
                setFilters({
                  ...filters,
                  ward_id: wardId,
                  ward_name: selectedWard ? selectedWard.name : "",
                });
                setCurrentPage(1);
              }}
              disabled={!filters.municipality_id}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[150px] disabled:opacity-50"
            >
              <option value="">All Wards</option>
              {wards.map((w) => (
                <option key={w.id} value={w.id}>
                  Ward {w.name}
                </option>
              ))}
            </select>
          </div>

          {/* Second Row - Search and Eligibility Status */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by Name, Mobile, Sanction order number"
                value={filters.search}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    search: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                className="px-4 py-2 pl-9 border-2 border-orange-400 dark:border-orange-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm w-full min-w-[450px] transition-all duration-200"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                <FiSearch className="w-4 h-4" />
              </span>
            </div>

            <select
              value={filters.eligibility_status}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  eligibility_status: e.target.value,
                });
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[160px]"
            >
              <option value="">All Status</option>
              <option value="Eligible">Eligible</option>
              <option value="Ineligible">Ineligible</option>{" "}
              <option value="Fit for Deletion">Fit for Deletion</option>
            </select>

            {(filters.district_id ||
              filters.municipality_id ||
              filters.search ||
              filters.eligibility_status) && (
              <button
                onClick={() => {
                  setFilters({
                    district_id: "",
                    municipality_id: "",
                    ward_id: "",
                    ward_name: "",
                    search: "",
                    eligibility_status: "",
                  });
                  setCurrentPage(1);
                  dispatch(clearMunicipalities());
                  dispatch(clearWards());
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors text-sm flex items-center gap-1"
              >
                <FiX className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        </div>
        {/* {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-800 dark:text-gray-600">
              Showing {(pagination.page - 1) * limit + 1} to{" "}
              {Math.min(pagination.page * limit, pagination.totalRecords)} of{" "}
              {pagination.totalRecords} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-800 dark:text-gray-600"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium">
                {currentPage}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-800 dark:text-gray-600"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )} */}

        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 pb-4 border-t border-gray-200 dark:border-gray-700">
            {" "}
            {/* Left side - Showing entries info */}
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {(pagination.page - 1) * pagination.limit + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {Math.min(
                  pagination.page * pagination.limit,
                  pagination.totalRecords,
                )}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {pagination.totalRecords}
              </span>{" "}
              entries
            </div>
            {/* Right side - Pagination controls */}
            <div className="flex items-center gap-2">
              {/* First Page */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronsLeft className="w-4 h-4" />
              </button>

              {/* Previous */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1.5">
                {(() => {
                  const totalPages = pagination.totalPages;
                  const current = currentPage;
                  const pages = [];
                  const maxVisible = 5;

                  if (totalPages <= maxVisible) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    if (current <= 3) {
                      pages.push(1, 2, 3, 4, "...", totalPages);
                    } else if (current >= totalPages - 2) {
                      pages.push(
                        1,
                        "...",
                        totalPages - 3,
                        totalPages - 2,
                        totalPages - 1,
                        totalPages,
                      );
                    } else {
                      pages.push(
                        1,
                        "...",
                        current - 1,
                        current,
                        current + 1,
                        "...",
                        totalPages,
                      );
                    }
                  }

                  return pages.map((page, index) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-2 text-gray-500 dark:text-gray-400"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          current === page
                            ? "bg-orange-600 text-white shadow-md shadow-orange-500/30"
                            : "border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  );
                })()}
              </div>

              {/* Next */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>

              {/* Last Page */}
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-4 border-orange-600 dark:border-orange-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : surveys.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/30">
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      ID
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[150px]">
                      Applicant Name
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[170px]">
                      Address
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Pincode
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[110px]">
                      Scheme
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[100px]">
                      Age/Gender
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[120px]">
                      Mobile
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[90px]">
                      Caste
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[130px]">
                      Father's Name
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[150px]">
                      District
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[170px]">
                      Municipality
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[70px]">
                      Ward
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Resident of ULB
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Constituency
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[120px]">
                      Survey Officer
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[80px]">
                      DLS status
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[80px]">
                      Part no.
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[80px]">
                      Serial no.
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium    border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[150px]">
                      BPL ID
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium    border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[150px]">
                      Sanction order no.
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[150px]">
                      Ration card no.
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[150px]">
                      Epic no.
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[150px]">
                      Beneficiary ID.
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[150px]">
                      Beneficiary no.
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Geo Tagged
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[150px]">
                      Latitude & Longitude
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[120px]">
                      Eligibility
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Ineligibility reason
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Voter Card
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[130px]">
                      Valid Voter Card
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[170px]">
                      Not Collected Reason (Voter)
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Aadhar Card
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[160px]">
                      Valid Aadhaar Card
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[160px]">
                      Not Collected Reason (AAdhar)
                    </th>
                    {/* <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      DLC
                    </th> */}
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[160px]">
                      Receives other Govt. pension
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[170px]">
                      Other Govt. pension name
                    </th>
                    {/* <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[160px]">
                      Other Govt. pension
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[160px]">
                      Other Govt. remarks
                    </th> */}
                    {/* <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Ack copy
                    </th> */}
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium  border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 min-w-[160px]">
                      Comments
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400  min-w-[160px]">
                      DLC expiry date
                    </th>
                    <th className="text-center uppercase italic text-base py-1.5 px-2   text-center text-xs font-medium border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400">
                      Updated Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {surveys.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="py-1.5 px-2   text-center text-xs text-gray-500 dark:text-gray-600 border border-gray-300 dark:border-gray-600">
                        {/* {(pagination.page - 1) * pagination.limit + index + 1} */}
                        {item.id || "-"}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "applicant_name").isChanged,
                        )} text-center text-xs font-medium`}
                      >
                        {" "}
                        {(() => {
                          const field = getFieldValue(item, "applicant_name");
                          return field.isChanged ? (
                            <div>
                              <span className="text-gray-900 dark:text-white">
                                {field.value || "N/A"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-900 dark:text-white">
                              {field.value || "N/A"}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "address").isChanged,
                        )} text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {" "}
                        {(() => {
                          const addressField = getFieldValue(item, "address");

                          return addressField.isChanged ? (
                            <div>
                              <span>{addressField.value || "N/A"}</span>
                              {addressField.oldValue && (
                                <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                  (was: {addressField.oldValue})
                                </div>
                              )}
                            </div>
                          ) : (
                            <span>{addressField.value || "N/A"}</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "pincode").isChanged,
                        )} text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {" "}
                        {(() => {
                          const pincodeField = getFieldValue(item, "pincode");

                          return pincodeField.isChanged ? (
                            <div>
                              <span>{pincodeField.value || "N/A"}</span>
                              {pincodeField.oldValue && (
                                <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                  (was: {pincodeField.oldValue})
                                </div>
                              )}
                            </div>
                          ) : (
                            <span>{pincodeField.value || "N/A"}</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "scheme").isChanged,
                        )} py-1.5 px-2   text-center text-xs border border-gray-300 dark:border-gray-600`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "scheme");
                          return field.isChanged ? (
                            <div>
                              <span className="text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600">
                                {field.value || "N/A"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600">
                              {field.value || "N/A"}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "age").isChanged ||
                            getFieldValue(item, "gender").isChanged,
                        )} text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {" "}
                        {(() => {
                          const ageField = getFieldValue(item, "age");
                          const genderField = getFieldValue(item, "gender");
                          const hasChange =
                            ageField.isChanged || genderField.isChanged;
                          const age = ageField.value || "N/A";
                          const gender = genderField.value || "N/A";

                          return hasChange ? (
                            <div>
                              <span>
                                {age} / {gender}
                              </span>
                              {(ageField.oldValue || genderField.oldValue) && (
                                <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                  (was:
                                  {ageField.oldValue && ` ${ageField.oldValue}`}
                                  {genderField.oldValue &&
                                    ` / ${genderField.oldValue}`}
                                  )
                                </div>
                              )}
                            </div>
                          ) : (
                            <span>
                              {age} / {gender}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "mobile_no").isChanged,
                        )} text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {" "}
                        {(() => {
                          const field = getFieldValue(item, "mobile_no");
                          return field.isChanged ? (
                            <div>
                              <div className="flex items-center gap-1.5">
                                <FiPhone className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                                <span>{field.value || "N/A"}</span>
                              </div>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <FiPhone className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                              <span>{field.value || "N/A"}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "caste").isChanged,
                        )} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "caste");
                          return field.isChanged ? (
                            <div>
                              <span>{field.value || "N/A"}</span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span>{field.value || "N/A"}</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "fh_name").isChanged,
                        )} text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {" "}
                        {(() => {
                          const field = getFieldValue(item, "fh_name");
                          return field.isChanged ? (
                            <div>
                              <span>{field.value || "N/A"}</span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span>{field.value || "N/A"}</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "district_id").isChanged,
                        )} text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {" "}
                        {(() => {
                          const field = getFieldValue(item, "district_id");
                          const districtName = item.district_name || "N/A";
                          return field.isChanged ? (
                            <div>
                              <span>{districtName}</span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: district changed)
                              </div>
                            </div>
                          ) : (
                            <span>{districtName}</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "municipality_id").isChanged,
                        )} py-1.5 px-2  text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "municipality_id");
                          const municipalityName =
                            item.municipality_name || "N/A";
                          return field.isChanged ? (
                            <div>
                              <span>{municipalityName}</span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: municipality changed)
                              </div>
                            </div>
                          ) : (
                            <span>{municipalityName}</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "ward").isChanged,
                        )} py-1.5 px-2  text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "ward");
                          return field.isChanged ? (
                            <div>
                              <span>{field.value || "N/A"}</span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span>{field.value || "N/A"}</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "resident_of_ulb").isChanged,
                        )} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "resident_of_ulb");
                          const value = field.value || "N/A";
                          return field.isChanged ? (
                            <div>
                              <span
                                className={`font-mono uppercase font-semibold ${
                                  value === "Yes"
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-red-600 dark:text-red-400"
                                }`}
                              >
                                {value}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span
                              className={`font-mono uppercase font-semibold ${
                                value === "Yes"
                                  ? "text-green-600 dark:text-green-400"
                                  : "text-red-600 dark:text-red-400"
                              }`}
                            >
                              {value}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "constituency").isChanged,
                        )} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "constituency");
                          return field.isChanged ? (
                            <div>
                              <span>{field.value || "N/A"}</span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span>{field.value || "N/A"}</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "survey_officer_id").isChanged,
                        )} text-left text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {" "}
                        {item.survey_officer_name || "N/A"}
                        {item.survey_officer_id && (
                          <div className="text-xs text-gray-600 dark:text-gray-500 mt-0.5">
                            ID: {item.survey_officer_id}
                          </div>
                        )}
                        {item.survey_officer_phone && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <FiPhone className="w-3.5 h-3.5 text-orange-500 text-xs" />
                            <span className="italic text-gray-800 dark:text-gray-300 text-xs">
                              {item.survey_officer_phone}
                            </span>
                          </div>
                        )}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "dls_status").isChanged,
                        )} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "dls_status");
                          const value = field.value || "N/A";
                          const isActive =
                            value === "Yes" || value === "Active";

                          return field.isChanged ? (
                            <div className="flex flex-col items-center w-full">
                              <span
                                className={`inline-block px-2 py-0.5 rounded-full font-medium ${
                                  isActive
                                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                    : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                }`}
                              >
                                {isActive ? "Active" : "Inactive"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5 text-center">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full font-medium ${
                                isActive
                                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                  : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                              }`}
                            >
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "part_no").isChanged,
                        )} text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "part_no");
                          return field.isChanged ? (
                            <div>
                              <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                                {field.value || "N/A"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                              {field.value || "N/A"}
                            </span>
                          );
                        })()}
                      </td>

                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "srl_no").isChanged,
                        )} text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "srl_no");
                          return field.isChanged ? (
                            <div>
                              <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                                {field.value || "N/A"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                              {field.value || "N/A"}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(
                          getFieldValue(item, "bpl_id").isChanged,
                        )} text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {" "}
                        {item.bpl_id ? (
                          <div className="flex flex-col">
                            <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                              {item.bpl_id}
                            </span>
                            {item.bpl_member_id && (
                              <span className="font-mono text-sm text-gray-500 dark:text-gray-600 mt-0.5">
                                Member ID: {item.bpl_member_id}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-600">N/A</span>
                        )}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "sanction_order_no").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "sanction_order_no",
                          );
                          return field.isChanged ? (
                            <div>
                              <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                                {field.value || "N/A"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                              {field.value || "N/A"}
                            </span>
                          );
                        })()}
                      </td>

                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "ration_card_no").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "ration_card_no");
                          return field.isChanged ? (
                            <div>
                              <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                                {field.value || "N/A"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                              {field.value || "N/A"}
                            </span>
                          );
                        })()}
                      </td>

                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "epic_no").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "epic_no");
                          return field.isChanged ? (
                            <div>
                              <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                                {field.value || "N/A"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="font-mono text-sm text-gray-700 dark:text-gray-300">
                              {field.value || "N/A"}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "beneficiary_id").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "beneficiary_id");
                          return field.isChanged ? (
                            <div>
                              <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                                {field.value || "N/A"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                              {field.value || "N/A"}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "beneficiary_no").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "beneficiary_no");
                          return field.isChanged ? (
                            <div>
                              <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                                {field.value || "N/A"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                              {field.value || "N/A"}
                            </span>
                          );
                        })()}
                      </td>

                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "geo_tagged_photo_captured").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "geo_tagged_photo_captured",
                          );
                          return field.isChanged ? (
                            <div>
                              {field.value ? (
                                <img
                                  src={field.value}
                                  alt="Geo tagged"
                                  className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-80 border border-gray-200 dark:border-gray-600 mx-auto"
                                  onClick={() =>
                                    setImageModal({
                                      open: true,
                                      url: field.value,
                                      title: "Geo tagged",
                                    })
                                  }
                                />
                              ) : (
                                <span className="text-gray-600">N/A</span>
                              )}
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : field.value ? (
                            <img
                              src={field.value}
                              alt="Geo tagged"
                              className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-80 border border-gray-200 dark:border-gray-600 mx-auto"
                              onClick={() =>
                                setImageModal({
                                  open: true,
                                  url: field.value,
                                  title: "Geo tagged",
                                })
                              }
                            />
                          ) : (
                            <span className="text-gray-600">N/A</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "latitude").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {item.latitude ? (
                          <>
                            <div>
                              Lat:{" "}
                              <span className="italic">{item.latitude}</span>
                            </div>
                            <div>
                              Long:{" "}
                              <span className="italic">{item.longitude}</span>
                            </div>
                          </>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "eligibility_status").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "eligibility_status",
                          );
                          const value = field.value || "N/A";
                          const isEligible =
                            value?.toLowerCase() === "eligible";
                          return field.isChanged ? (
                            <div>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                  isEligible
                                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                    : value?.toLowerCase() ===
                                        "Fit for Deletion"
                                      ? "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400"
                                      : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                }`}
                              >
                                {value}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                isEligible
                                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                  : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                              }`}
                            >
                              {value}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "ineligible_reason").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "ineligible_reason",
                          );
                          return field.isChanged ? (
                            <div>
                              <span>{field.value || "N/A"}</span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span>{field.value || "N/A"}</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "voter_card_collected").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "voter_card_collected",
                          );
                          return field.isChanged ? (
                            <div>
                              {field.value ? (
                                <img
                                  src={field.value}
                                  alt="Voter Card"
                                  className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 border border-gray-200 dark:border-gray-600 mx-auto"
                                  onClick={() =>
                                    setImageModal({
                                      open: true,
                                      url: field.value,
                                      title: "Voter Card",
                                    })
                                  }
                                />
                              ) : (
                                <span className="text-gray-600">N/A</span>
                              )}
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : field.value ? (
                            <img
                              src={field.value}
                              alt="Voter Card"
                              className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 border border-gray-200 dark:border-gray-600 mx-auto"
                              onClick={() =>
                                setImageModal({
                                  open: true,
                                  url: field.value,
                                  title: "Voter Card",
                                })
                              }
                            />
                          ) : (
                            <span className="text-gray-600">N/A</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "valid_voter_card").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(item, "valid_voter_card");
                          const value = field.value || "N/A";
                          return field.isChanged ? (
                            <div>
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                {value}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              {value}
                            </span>
                          );
                        })()}
                      </td>

                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "no_voter_card_collected_reason").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {item.no_voter_card_collected_reason || "-"}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "aadhar_card_collected").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "aadhar_card_collected",
                          );
                          return field.isChanged ? (
                            <div>
                              {field.value ? (
                                <img
                                  src={field.value}
                                  alt="Aadhaar Card"
                                  className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 border border-gray-200 dark:border-gray-600 mx-auto"
                                  onClick={() =>
                                    setImageModal({
                                      open: true,
                                      url: field.value,
                                      title: "Aadhaar Card",
                                    })
                                  }
                                />
                              ) : (
                                <span className="text-gray-600">N/A</span>
                              )}
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : field.value ? (
                            <img
                              src={field.value}
                              alt="Aadhaar Card"
                              className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 border border-gray-200 dark:border-gray-600 mx-auto"
                              onClick={() =>
                                setImageModal({
                                  open: true,
                                  url: field.value,
                                  title: "Aadhaar Card",
                                })
                              }
                            />
                          ) : (
                            <span className="text-gray-600">N/A</span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "valid_aadhar_card").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "valid_aadhar_card",
                          );
                          const value = field.value || "N/A";
                          return field.isChanged ? (
                            <div>
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                {value}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              {value}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "no_aadhar_card_collected_reason").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "no_aadhar_card_collected_reason",
                          );
                          return field.isChanged ? (
                            <div>
                              <span className="text-gray-800 dark:text-gray-300">
                                {field.value || "-"}
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "-"})
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-800 dark:text-gray-300">
                              {field.value || "-"}
                            </span>
                          );
                        })()}
                      </td>
                      {/* Govt. Pension */}
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "other_govt_pension").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {item.other_govt_pension ? (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              item.other_govt_pension.toLowerCase() === "yes"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : item.other_govt_pension.toLowerCase() === "no"
                                  ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                  : ""
                            }`}
                          >
                            {item.other_govt_pension}
                          </span>
                        ) : (
                          <span>N/A</span>
                        )}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "other_govt_pension_name").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        <span
                          className={
                            item.other_govt_pension_name
                              ? "text-green-600 dark:text-green-400 font-medium"
                              : ""
                          }
                        >
                          {item.other_govt_pension_name || "N/A"}
                        </span>
                      </td>
                      {/* Govt. Pension Remarks*/}
                      {/* <td
                        className={`${getChangedCellClass(getFieldValue(item, "other_govt_pension_remarks").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {item.other_govt_pension_remarks ? (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              item.other_govt_pension_remarks.toLowerCase() ===
                              "yes"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : item.other_govt_pension_remarks.toLowerCase() ===
                                    "no"
                                  ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                  : ""
                            }`}
                          >
                            {item.other_govt_pension_remarks}
                          </span>
                        ) : (
                          <span>N/A</span>
                        )}
                      </td> */}

                      {/* Other Govt. Pension Uploaded Image */}
                      {/* <td
                        className={`${getChangedCellClass(getFieldValue(item, "other_govt_pension_uploaded").isChanged)} py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "other_govt_pension_uploaded",
                          );
                          return field.isChanged ? (
                            <div>
                              {field.value ? (
                                <img
                                  src={field.value}
                                  alt="Govt. Pension"
                                  className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 border border-gray-200 dark:border-gray-600 mx-auto"
                                  onClick={() =>
                                    setImageModal({
                                      open: true,
                                      url: field.value,
                                      title: "Other Govt. Pension",
                                    })
                                  }
                                />
                              ) : (
                                <span className="text-gray-600">N/A</span>
                              )}
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: {field.oldValue || "N/A"})
                              </div>
                            </div>
                          ) : field.value ? (
                            <img
                              src={field.value}
                              alt="Govt. Pension"
                              className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 border border-gray-200 dark:border-gray-600 mx-auto"
                              onClick={() =>
                                setImageModal({
                                  open: true,
                                  url: field.value,
                                  title: "Other Govt. Pension",
                                })
                              }
                            />
                          ) : (
                            <span className="text-gray-600">N/A</span>
                          );
                        })()}
                      </td> */}

                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "specific_comments").isChanged)} py-1.5 px-2   text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {(() => {
                          const field = getFieldValue(
                            item,
                            "specific_comments",
                          );
                          return field.isChanged ? (
                            <div>
                              <span className="italic text-gray-700 dark:text-gray-300">
                                "{field.value || "N/A"}"
                              </span>
                              <div className="text-xs italic text-gray-600 dark:text-gray-500 mt-0.5">
                                (was: "{field.oldValue || "N/A"}")
                              </div>
                            </div>
                          ) : (
                            <span className="italic text-gray-700 dark:text-gray-300">
                              "{field.value || "N/A"}"
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "dlc_expired_date").isChanged)} py-1.5 px-2   text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {item.dlc_expired_date ? (
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                            {new Date(item.dlc_expired_date).toLocaleDateString(
                              "en-IN",
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-600">N/A</span>
                        )}
                      </td>
                      <td
                        className={`${getChangedCellClass(getFieldValue(item, "updated_at").isChanged)} py-1.5 px-2   text-center text-xs text-gray-800 dark:text-gray-300`}
                      >
                        {item.updated_at ? (
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                            {new Date(item.updated_at).toLocaleDateString(
                              "en-IN",
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-600">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                {/* Left side - Showing entries info */}
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.totalRecords,
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {pagination.totalRecords}
                  </span>{" "}
                  entries
                </div>

                {/* Right side - Pagination controls */}
                <div className="flex items-center gap-2">
                  {/* First Page */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronsLeft className="w-4 h-4" />
                  </button>

                  {/* Previous */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const totalPages = pagination.totalPages;
                      const current = currentPage;
                      const pages = [];
                      const maxVisible = 5;

                      if (totalPages <= maxVisible) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        if (current <= 3) {
                          pages.push(1, 2, 3, 4, "...", totalPages);
                        } else if (current >= totalPages - 2) {
                          pages.push(
                            1,
                            "...",
                            totalPages - 3,
                            totalPages - 2,
                            totalPages - 1,
                            totalPages,
                          );
                        } else {
                          pages.push(
                            1,
                            "...",
                            current - 1,
                            current,
                            current + 1,
                            "...",
                            totalPages,
                          );
                        }
                      }

                      return pages.map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-2 text-gray-500 dark:text-gray-400"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                              current === page
                                ? "bg-orange-600 text-white shadow-md shadow-orange-500/30"
                                : "border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {page}
                          </button>
                        ),
                      );
                    })()}
                  </div>

                  {/* Next */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <FiUsers className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-600">
              No beneficiary surveys found
            </p>
          </div>
        )}
      </div>
      {/* Image Modal */}
      {imageModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setImageModal({ open: false, url: "", title: "" })}
        >
          <div className="relative max-w-3xl w-full mx-4">
            <button
              onClick={() => setImageModal({ open: false, url: "", title: "" })}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl"
            >
              <FiX className="w-6 h-6" />
            </button>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-2">
              <img
                src={imageModal.url}
                alt={imageModal.title}
                className="w-full h-auto max-h-[80vh] object-contain rounded"
              />
              <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-300 py-2">
                {imageModal.title}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeneficiarySurvey;
